import { useEffect, useRef, useCallback } from 'react';
import { Application, Container, Graphics, Ticker } from 'pixi.js';
import {
  PLAYER_WIDTH,
  PLAYER_HEIGHT,
  PLAYER_COLOR,
  PLAYER_SPEED,
  BLOCK_WIDTH,
  BLOCK_HEIGHT,
  BLOCK_COLOR,
  INITIAL_FALL_SPEED,
  INITIAL_SPAWN_INTERVAL,
  SCORE_PER_SECOND,
  DIFFICULTY_SCALE,
  PARTICLE_CONFIG,
  SCREEN_SHAKE_CONFIG,
  WORLD_CONFIG,
  COLORS,
} from '@/game/configs';
import { useGameAudio } from '@/hooks/useGameAudio';
interface FallingBlock {
  x: number;
  y: number;
  speed: number;
  graphics: Graphics;
}
interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  alpha: number;
  scale: number;
  color: number;
  graphics: Graphics;
}
interface GameCanvasProps {
  onScoreUpdate: (delta: number) => void;
  onGameOver: () => void;
  isPlaying: boolean;
}
interface ShakeState {
  offsetX: number;
  offsetY: number;
  intensity: number;
  startTime: number;
}
// Custom type to handle destroyed state
interface AppWithDestroyed extends Application {
  destroyed?: boolean;
}
export function GameCanvas({ onScoreUpdate, onGameOver, isPlaying }: GameCanvasProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const appRef = useRef<AppWithDestroyed | null>(null);
  const stageRef = useRef<Container | null>(null);
  const playerRef = useRef<Container | null>(null);
  const blocksRef = useRef<FallingBlock[]>([]);
  const particlesRef = useRef<Particle[]>([]);
  const keysRef = useRef<Set<string>>(new Set());
  const touchRef = useRef<{ startX: number; currentX: number } | null>(null);
  const lastSpawnRef = useRef(0);
  const currentSpeedRef = useRef(INITIAL_FALL_SPEED);
  const currentSpawnIntervalRef = useRef(INITIAL_SPAWN_INTERVAL);
  const gameActiveRef = useRef(false);
  const canvasWidthRef = useRef(WORLD_CONFIG.baseWidth);
  const canvasHeightRef = useRef(WORLD_CONFIG.baseHeight);
  const shakeRef = useRef<ShakeState>({
    offsetX: 0,
    offsetY: 0,
    intensity: 0,
    startTime: 0,
  });
  const lastScoreTimeRef = useRef(0);
  const { playHit, playScore } = useGameAudio();
  const createPlayer = useCallback(() => {
    const container = new Container();
    const graphics = new Graphics();
    graphics.roundRect(
      -PLAYER_WIDTH / 2,
      -PLAYER_HEIGHT / 2,
      PLAYER_WIDTH,
      PLAYER_HEIGHT,
      12
    );
    graphics.fill(PLAYER_COLOR);
    graphics.roundRect(
      -PLAYER_WIDTH / 2 + 4,
      -PLAYER_HEIGHT / 2 + 4,
      12,
      12,
      4
    );
    graphics.fill(0x60A5FA);
    graphics.roundRect(
      PLAYER_WIDTH / 2 - 16,
      -PLAYER_HEIGHT / 2 + 4,
      12,
      12,
      4
    );
    graphics.fill(0x60A5FA);
    container.addChild(graphics);
    container.y =
      canvasHeightRef.current -
      WORLD_CONFIG.groundHeight -
      PLAYER_HEIGHT / 2 -
      10;
    return container;
  }, []);
  const createBlock = useCallback((x: number): FallingBlock => {
    const graphics = new Graphics();
    graphics.roundRect(
      -BLOCK_WIDTH / 2,
      -BLOCK_HEIGHT / 2,
      BLOCK_WIDTH,
      BLOCK_HEIGHT,
      8
    );
    graphics.fill(BLOCK_COLOR);
    graphics.roundRect(
      -BLOCK_WIDTH / 2 + 4,
      -BLOCK_HEIGHT / 2 + 4,
      8,
      8,
      3
    );
    graphics.fill(0xF87171);
    const block: FallingBlock = {
      x,
      y: -BLOCK_HEIGHT,
      speed: currentSpeedRef.current,
      graphics,
    };
    return block;
  }, []);
  const createParticles = useCallback((x: number, y: number) => {
    const { count, speed, colors, sizeVariance } = PARTICLE_CONFIG;
    for (let i = 0; i < count; i++) {
      const angle = (Math.PI * 2 * i) / count + Math.random() * 0.8;
      const velocity = speed * (0.4 + Math.random() * 0.8);
      const color = colors[Math.floor(Math.random() * colors.length)];
      const size = 2 + Math.random() * sizeVariance;
      const graphics = new Graphics();
      graphics.circle(0, 0, size);
      graphics.fill(color);
      const particle: Particle = {
        x,
        y,
        vx: Math.cos(angle) * velocity,
        vy: Math.sin(angle) * velocity - 2,
        alpha: 1,
        scale: 1,
        color,
        graphics,
      };
      particlesRef.current.push(particle);
    }
  }, []);
  const triggerScreenShake = useCallback(() => {
    shakeRef.current = {
      offsetX: 0,
      offsetY: 0,
      intensity: SCREEN_SHAKE_CONFIG.intensity,
      startTime: performance.now(),
    };
  }, []);
  const checkCollision = useCallback(
    (
      px: number,
      py: number,
      bx: number,
      by: number
    ): boolean => {
      const playerLeft = px - PLAYER_WIDTH / 2 + 8;
      const playerRight = px + PLAYER_WIDTH / 2 - 8;
      const playerTop = py - PLAYER_HEIGHT / 2 + 8;
      const playerBottom = py + PLAYER_HEIGHT / 2 - 8;
      const blockLeft = bx - BLOCK_WIDTH / 2;
      const blockRight = bx + BLOCK_WIDTH / 2;
      const blockTop = by - BLOCK_HEIGHT / 2;
      const blockBottom = by + BLOCK_HEIGHT / 2;
      return !(
        playerRight < blockLeft ||
        playerLeft > blockRight ||
        playerBottom < blockTop ||
        playerTop > blockBottom
      );
    },
    []
  );
  const gameLoop = useCallback(
    (ticker: Ticker) => {
      if (!gameActiveRef.current) return;
      const deltaTime = ticker.deltaMS / 1000;
      const app = appRef.current;
      const stage = stageRef.current;
      if (!app || !stage || !playerRef.current) return;
      const player = playerRef.current;
      const width = canvasWidthRef.current;
      // Handle keyboard input
      if (keysRef.current.has('ArrowLeft') || keysRef.current.has('KeyA')) {
        player.x = Math.max(PLAYER_WIDTH / 2, player.x - PLAYER_SPEED);
      }
      if (keysRef.current.has('ArrowRight') || keysRef.current.has('KeyD')) {
        player.x = Math.min(width - PLAYER_WIDTH / 2, player.x + PLAYER_SPEED);
      }
      // Handle touch input
      if (touchRef.current) {
        const touchDelta = touchRef.current.currentX - touchRef.current.startX;
        player.x = Math.max(
          PLAYER_WIDTH / 2,
          Math.min(width - PLAYER_WIDTH / 2, player.x + touchDelta * 2)
        );
        touchRef.current.startX = touchRef.current.currentX;
      }
      // Spawn new blocks
      const now = performance.now();
      if (now - lastSpawnRef.current > currentSpawnIntervalRef.current) {
        const spawnX = BLOCK_WIDTH / 2 + Math.random() * (width - BLOCK_WIDTH);
        const newBlock = createBlock(spawnX);
        newBlock.x = spawnX;
        blocksRef.current.push(newBlock);
        app.stage.addChild(newBlock.graphics);
        lastSpawnRef.current = now;
      }
      // Update blocks
      for (let i = blocksRef.current.length - 1; i >= 0; i--) {
        const block = blocksRef.current[i];
        block.y += block.speed;
        block.graphics.x = block.x;
        block.graphics.y = block.y;
        if (checkCollision(player.x, player.y, block.x, block.y)) {
          createParticles(player.x, player.y);
          triggerScreenShake();
          playHit();
          onGameOver();
          gameActiveRef.current = false;
          return;
        }
        if (block.y > canvasHeightRef.current + BLOCK_HEIGHT) {
          app.stage.removeChild(block.graphics);
          block.graphics.destroy();
          blocksRef.current.splice(i, 1);
        }
      }
      // Update particles
      for (let i = particlesRef.current.length - 1; i >= 0; i--) {
        const particle = particlesRef.current[i];
        particle.x += particle.vx;
        particle.y += particle.vy;
        particle.vy += 0.25;
        particle.alpha -= 0.018;
        particle.scale *= 0.98;
        particle.graphics.x = particle.x;
        particle.graphics.y = particle.y;
        particle.graphics.alpha = particle.alpha;
        particle.graphics.scale.set(particle.scale);
        if (particle.alpha <= 0) {
          app.stage.removeChild(particle.graphics);
          particle.graphics.destroy();
          particlesRef.current.splice(i, 1);
        }
      }
      // Update screen shake
      const shake = shakeRef.current;
      if (shake.intensity > SCREEN_SHAKE_CONFIG.minThreshold) {
        const elapsed = performance.now() - shake.startTime;
        if (elapsed < SCREEN_SHAKE_CONFIG.duration) {
          shake.offsetX = (Math.random() - 0.5) * shake.intensity * 2;
          shake.offsetY = (Math.random() - 0.5) * shake.intensity * 2;
          shake.intensity *= SCREEN_SHAKE_CONFIG.decay;
        } else {
          shake.offsetX = 0;
          shake.offsetY = 0;
          shake.intensity = 0;
        }
        stage.x = shake.offsetX;
        stage.y = shake.offsetY;
      }
      // Update score
      const scoreDelta = SCORE_PER_SECOND * deltaTime;
      onScoreUpdate(scoreDelta);
      // Play score sound periodically
      if (now - lastScoreTimeRef.current > 500) {
        lastScoreTimeRef.current = now;
      }
      // Increase difficulty
      currentSpeedRef.current = Math.min(
        DIFFICULTY_SCALE.maxSpeed,
        currentSpeedRef.current * DIFFICULTY_SCALE.speedMultiplier
      );
      currentSpawnIntervalRef.current = Math.max(
        DIFFICULTY_SCALE.minSpawnInterval,
        currentSpawnIntervalRef.current * DIFFICULTY_SCALE.spawnMultiplier
      );
    },
    [
      createBlock,
      createParticles,
      checkCollision,
      triggerScreenShake,
      playHit,
      onGameOver,
      onScoreUpdate,
    ]
  );
  const resetGame = useCallback(() => {
    const app = appRef.current;
    if (!app) return;
    for (const block of blocksRef.current) {
      app.stage.removeChild(block.graphics);
      block.graphics.destroy();
    }
    blocksRef.current = [];
    for (const particle of particlesRef.current) {
      app.stage.removeChild(particle.graphics);
      particle.graphics.destroy();
    }
    particlesRef.current = [];
    if (playerRef.current) {
      playerRef.current.x = canvasWidthRef.current / 2;
    }
    currentSpeedRef.current = INITIAL_FALL_SPEED;
    currentSpawnIntervalRef.current = INITIAL_SPAWN_INTERVAL;
    lastSpawnRef.current = performance.now();
    lastScoreTimeRef.current = performance.now();
    shakeRef.current = {
      offsetX: 0,
      offsetY: 0,
      intensity: 0,
      startTime: 0,
    };
    if (stageRef.current) {
      stageRef.current.x = 0;
      stageRef.current.y = 0;
    }
  }, []);
  useEffect(() => {
    if (!containerRef.current || !isPlaying) return;
    let cancelled = false;
    let cleanup: (() => void) | undefined;
    const init = async () => {
      if (cancelled || !containerRef.current) return;
      const app = new Application() as AppWithDestroyed;
      await app.init({
        background: COLORS.background,
        resizeTo: containerRef.current,
        antialias: true,
        resolution: Math.min(window.devicePixelRatio, 2),
        autoDensity: true,
      });
      if (cancelled) {
        app.destroy(true, { children: true });
        return;
      }
      containerRef.current.appendChild(app.canvas);
      appRef.current = app;
      const stage = new Container();
      app.stage.addChild(stage);
      stageRef.current = stage;
      const player = createPlayer();
      player.x = canvasWidthRef.current / 2;
      stage.addChild(player);
      playerRef.current = player;
      // Add ground
      const ground = new Graphics();
      ground.rect(
        0,
        canvasHeightRef.current - WORLD_CONFIG.groundHeight,
        canvasWidthRef.current,
        WORLD_CONFIG.groundHeight
      );
      ground.fill(0xe5ddd0);
      stage.addChild(ground);
      // Resize observer
      const resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const { width, height } = entry.contentRect;
          canvasWidthRef.current = width;
          canvasHeightRef.current = height;
          if (playerRef.current) {
            playerRef.current.y =
              height - WORLD_CONFIG.groundHeight - PLAYER_HEIGHT / 2 - 10;
            playerRef.current.x = Math.min(
              Math.max(PLAYER_WIDTH / 2, playerRef.current.x),
              width - PLAYER_WIDTH / 2
            );
          }
          ground.clear();
          ground.rect(0, height - WORLD_CONFIG.groundHeight, width, WORLD_CONFIG.groundHeight);
          ground.fill(0xe5ddd0);
        }
      });
      resizeObserver.observe(containerRef.current);
      app.ticker.add(gameLoop);
      cleanup = () => {
        resizeObserver.disconnect();
        app.ticker.remove(gameLoop);
      };
    };
    init();
    return () => {
      cancelled = true;
      if (cleanup) cleanup();
    };
  }, [isPlaying, createPlayer, gameLoop]);
  useEffect(() => {
    if (isPlaying) {
      resetGame();
      gameActiveRef.current = true;
      lastSpawnRef.current = performance.now();
      lastScoreTimeRef.current = performance.now();
    } else {
      gameActiveRef.current = false;
    }
  }, [isPlaying, resetGame]);
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (['ArrowLeft', 'ArrowRight', 'KeyA', 'KeyD'].includes(e.code)) {
        e.preventDefault();
        keysRef.current.add(e.code);
      }
    };
    const handleKeyUp = (e: KeyboardEvent) => {
      keysRef.current.delete(e.code);
    };
    const handleTouchStart = (e: TouchEvent) => {
      if (e.touches.length === 1) {
        touchRef.current = {
          startX: e.touches[0].clientX,
          currentX: e.touches[0].clientX,
        };
      }
    };
    const handleTouchMove = (e: TouchEvent) => {
      e.preventDefault();
      if (touchRef.current && e.touches.length === 1) {
        touchRef.current.currentX = e.touches[0].clientX;
      }
    };
    const handleTouchEnd = () => {
      touchRef.current = null;
    };
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    window.addEventListener('touchstart', handleTouchStart, { passive: true });
    window.addEventListener('touchmove', handleTouchMove, { passive: false });
    window.addEventListener('touchend', handleTouchEnd);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, []);
  useEffect(() => {
    const app = appRef.current;
    return () => {
      if (app && !app.destroyed) {
        app.destroyed = true;
        try {
          app.renderer.events.setTargetElement(null);
          if (containerRef.current && app.canvas.parentNode === containerRef.current) {
            containerRef.current.removeChild(app.canvas);
          }
          app.destroy(true, { children: true });
        } catch (e) {
          // Ignore cleanup errors
        }
        appRef.current = null;
        playerRef.current = null;
        stageRef.current = null;
      }
    };
  }, []);
  return (
    <div
      ref={containerRef}
      className="w-full h-full"
      style={{
        position: 'absolute',
        inset: 0,
        touchAction: 'none',
      }}
    />
  );
}